// Thin typed client for the Wise API + bearer-token storage.

export interface User {
  id: string;
  email: string;
  full_name: string | null;
  status: string;
  mfa_enabled: boolean;
  /** May curate the Wise Exchange (publish/promote official connectors). */
  is_platform_admin: boolean;
  created_at: string;
}

export interface Tenant {
  id: string;
  slug: string;
  display_name: string;
  role: string;
}

export interface PendingRequest {
  tenant_slug: string;
  tenant_name: string;
}

export interface Me {
  user: User;
  tenants: Tenant[];
  pending_requests: PendingRequest[];
}

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? "/api").replace(/\/$/, "");

/** Supplies the Azure AD B2C access token; registered by the AuthProvider. */
let tokenProvider: (() => Promise<string | null>) | null = null;

export function setTokenProvider(fn: () => Promise<string | null>): void {
  tokenProvider = fn;
}

export class ApiError extends Error {
  readonly status: number;
  /** Per-field validation errors (config form 422s). */
  readonly fields?: Record<string, string>;
  constructor(status: number, message: string, fields?: Record<string, string>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.fields = fields;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  auth?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (!(options.body instanceof FormData)) headers["Content-Type"] = "application/json";
  if (options.auth && tokenProvider) {
    const token = await tokenProvider();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method: options.method ?? "GET",
    headers,
    body:
      options.body === undefined
        ? undefined
        : options.body instanceof FormData
          ? options.body
          : JSON.stringify(options.body),
  });

  if (!res.ok) {
    throw await errorFromResponse(res);
  }
  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

// FastAPI errors: {"detail": "..."} | {"detail": [{"msg": ...}]} (validation)
// | {"detail": {"message": ..., "fields": {...}}} (config form).
async function errorFromResponse(res: Response): Promise<ApiError> {
  let message = res.statusText || `HTTP ${res.status}`;
  let fields: Record<string, string> | undefined;
  try {
    const data = await res.json();
    const detail = (data as { detail?: unknown }).detail;
    if (typeof detail === "string") {
      message = detail;
    } else if (Array.isArray(detail)) {
      message = detail.map((d) => (d as { msg?: string }).msg ?? String(d)).join(", ");
    } else if (detail && typeof detail === "object") {
      const d = detail as { message?: string; fields?: Record<string, string> };
      message = d.message ?? message;
      if (d.fields && typeof d.fields === "object") fields = d.fields;
    }
  } catch {
    // non-JSON body
  }
  return new ApiError(res.status, message, fields);
}

export function fetchMe(): Promise<Me> {
  return request<Me>("/auth/me", { auth: true });
}

// ---- onboarding (no workspace yet) ----

export function createWorkspace(slug: string, display_name: string): Promise<Tenant> {
  return request<Tenant>("/auth/workspaces", {
    method: "POST",
    body: { slug, display_name },
    auth: true,
  });
}

export function lookupWorkspace(slug: string): Promise<{ slug: string; display_name: string }> {
  return request(`/auth/workspaces/lookup?slug=${encodeURIComponent(slug)}`, { auth: true });
}

export function requestAccess(slug: string, message = ""): Promise<{ status: string; detail: string }> {
  return request("/auth/workspaces/access-request", {
    method: "POST",
    body: { slug, message },
    auth: true,
  });
}

// ---- workspace administration ----

export interface WorkspaceInfo {
  slug: string;
  display_name: string;
  has_logo: boolean;
  plan: string;
  status: string;
}

export interface Member {
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
  status: string;
  joined_at: string;
}

export interface WorkspaceInvite {
  id: string;
  email: string;
  role: string;
  expires_at: string;
}

export interface MembersResponse {
  members: Member[];
  requests: Member[];
  invites: WorkspaceInvite[];
}

export const ROLES = ["owner", "admin", "builder", "operator", "viewer"] as const;

const ws = (tenant: string) => `/tenants/${tenant}/workspace`;

export function getWorkspace(tenant: string): Promise<WorkspaceInfo> {
  return request<WorkspaceInfo>(ws(tenant), { auth: true });
}

export function renameWorkspace(tenant: string, display_name: string): Promise<WorkspaceInfo> {
  return request<WorkspaceInfo>(ws(tenant), { method: "PATCH", body: { display_name }, auth: true });
}

/** Permanently delete a workspace. Owner only; `confirm` must equal the slug. */
export function deleteWorkspace(tenant: string, confirm: string): Promise<void> {
  return request<void>(`${ws(tenant)}?confirm=${encodeURIComponent(confirm)}`, {
    method: "DELETE",
    auth: true,
  });
}

export function listMembers(tenant: string): Promise<MembersResponse> {
  return request<MembersResponse>(`${ws(tenant)}/members`, { auth: true });
}

export function addMember(tenant: string, email: string, role: string): Promise<{ status: string; detail: string }> {
  return request(`${ws(tenant)}/members`, { method: "POST", body: { email, role }, auth: true });
}

export function approveMember(tenant: string, userId: string, role: string): Promise<unknown> {
  return request(`${ws(tenant)}/members/${userId}/approve`, { method: "POST", body: { role }, auth: true });
}

export function changeMemberRole(tenant: string, userId: string, role: string): Promise<unknown> {
  return request(`${ws(tenant)}/members/${userId}`, { method: "PATCH", body: { role }, auth: true });
}

export function removeMember(tenant: string, userId: string): Promise<void> {
  return request<void>(`${ws(tenant)}/members/${userId}`, { method: "DELETE", auth: true });
}

export function revokeInvite(tenant: string, inviteId: string): Promise<void> {
  return request<void>(`${ws(tenant)}/invites/${inviteId}`, { method: "DELETE", auth: true });
}

export function uploadLogo(tenant: string, file: File): Promise<WorkspaceInfo> {
  const form = new FormData();
  form.append("file", file);
  return request<WorkspaceInfo>(`${ws(tenant)}/logo`, { method: "PUT", body: form, auth: true });
}

export function deleteLogo(tenant: string): Promise<void> {
  return request<void>(`${ws(tenant)}/logo`, { method: "DELETE", auth: true });
}

/** URL for the workspace logo (auth'd fetch — use with `logoObjectUrl`). */
export async function logoObjectUrl(tenant: string): Promise<string | null> {
  const token = tokenProvider ? await tokenProvider() : null;
  if (!token) return null;
  const res = await fetch(`${API_BASE}${ws(tenant)}/logo`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;
  return URL.createObjectURL(await res.blob());
}

// ---- connections ----

export interface Connection {
  id: string;
  slug: string;
  display_name: string;
  system_type: string;
  status: string;
  capabilities: string[];
  xml_object_key: string;
  xml_version: number;
  bundle_sha256: string | null;
  created_at: string;
}

export type JobStatus = "queued" | "running" | "succeeded" | "failed" | "cancelled";

/** An AI generation run. Generation takes minutes, so the API returns one of
 *  these immediately and the client polls it. */
export interface GenerationJob {
  id: string;
  kind: "connector" | "modules";
  status: JobStatus;
  phase: string;
  progress: number;
  spec: string;
  connection_slug: string | null;
  connection: Connection | null;
  codegen_notes: string;
  test_detail: string;
  error: string | null;
  created_at: string;
  finished_at: string | null;
}

export const JOB_DONE: JobStatus[] = ["succeeded", "failed", "cancelled"];

export function isJobDone(job: GenerationJob): boolean {
  return JOB_DONE.includes(job.status);
}

export interface ConnectionCreatePayload {
  slug: string;
  display_name: string;
  system_type: string;
  params?: Record<string, unknown>;
  capabilities?: string[];
}

export interface FromChatPayload {
  /** Optional — the backend names the connector after the system by default. */
  slug?: string;
  display_name?: string;
  spec: string;
}

export function listConnections(tenant: string): Promise<Connection[]> {
  return request<Connection[]>(`/tenants/${tenant}/connections`, { auth: true });
}

export function getConnection(tenant: string, slug: string): Promise<Connection> {
  return request<Connection>(`/tenants/${tenant}/connections/${slug}`, { auth: true });
}

export function createConnection(tenant: string, body: ConnectionCreatePayload): Promise<Connection> {
  return request<Connection>(`/tenants/${tenant}/connections`, { method: "POST", body, auth: true });
}

/** Start generating a connector. Returns immediately with a job to poll. */
export function createFromChat(tenant: string, body: FromChatPayload): Promise<GenerationJob> {
  return request<GenerationJob>(`/tenants/${tenant}/connections/from-chat`, {
    method: "POST",
    body,
    auth: true,
  });
}

export function deleteConnection(tenant: string, slug: string): Promise<void> {
  return request<void>(`/tenants/${tenant}/connections/${slug}`, { method: "DELETE", auth: true });
}

/** Start adding modules/operations to an existing connector. Returns a job. */
export function addModules(tenant: string, slug: string, spec: string): Promise<GenerationJob> {
  return request<GenerationJob>(`/tenants/${tenant}/connections/${slug}/modules/from-chat`, {
    method: "POST",
    body: { spec },
    auth: true,
  });
}

/** Result of checking a connection's credentials against the live system. */
export interface ConnectionTestResult {
  ok: boolean;
  detail: string;
  latency_ms: number | null;
  trace: string;
  tested_at: string;
}

/** Run the connector's own test_connection() in the sandbox. */
export function testConnection(tenant: string, slug: string): Promise<ConnectionTestResult> {
  return request<ConnectionTestResult>(`/tenants/${tenant}/connections/${slug}/test`, {
    method: "POST",
    auth: true,
  });
}

export function getJob(tenant: string, jobId: string): Promise<GenerationJob> {
  return request<GenerationJob>(`/tenants/${tenant}/connections/jobs/${jobId}`, { auth: true });
}

export function listJobs(tenant: string): Promise<GenerationJob[]> {
  return request<GenerationJob[]>(`/tenants/${tenant}/connections/jobs`, { auth: true });
}

/** Poll `jobId` until it reaches a terminal state, reporting each update. */
export async function waitForJob(
  tenant: string,
  jobId: string,
  onUpdate: (job: GenerationJob) => void,
  signal?: AbortSignal,
): Promise<GenerationJob> {
  let delay = 1200;
  for (;;) {
    if (signal?.aborted) throw new DOMException("cancelled", "AbortError");
    const job = await getJob(tenant, jobId);
    onUpdate(job);
    if (isJobDone(job)) return job;
    await new Promise((r) => setTimeout(r, delay));
    delay = Math.min(delay * 1.25, 5000); // ease off; generation runs for minutes
  }
}

// ---- connection configuration ----

export type ParamKind = "string" | "multiline" | "number" | "boolean" | "url" | "enum";

export interface ParamSpec {
  name: string;
  label: string;
  description: string;
  kind: ParamKind;
  required: boolean;
  secret: boolean;
  default: string | null;
  choices: string[];
  placeholder: string;
  group: string;
  /** Show/require only while every referenced param's value is in the list. */
  visible_when: Record<string, string[]>;
}

export interface ConnectionConfig {
  schema: ParamSpec[];
  schema_source: "generated" | "preset" | "none";
  values: Record<string, string>;
  secrets_set: string[];
  config_version: number;
}

export function getConnectionConfig(tenant: string, slug: string): Promise<ConnectionConfig> {
  return request<ConnectionConfig>(`/tenants/${tenant}/connections/${slug}/config`, { auth: true });
}

export function updateConnectionConfig(
  tenant: string,
  slug: string,
  values: Record<string, string | boolean>,
): Promise<ConnectionConfig> {
  return request<ConnectionConfig>(`/tenants/${tenant}/connections/${slug}/config`, {
    method: "PUT",
    body: { values },
    auth: true,
  });
}

// ---- connector exchange ----

export interface ExchangeListing {
  id: string;
  slug: string;
  display_name: string;
  description: string;
  system_type: string;
  capabilities: string[];
  params_schema: ParamSpec[];
  version: number;
  install_count: number;
  has_code: boolean;
  /** Part of the curated Wise Exchange rather than a workspace's own listing. */
  is_official: boolean;
  /** simple-icons slug for the system logo, plus its brand colour (hex, no #). */
  icon_slug: string;
  brand_color: string;
  /** Catalog section key — see CATEGORY_LABELS. */
  category: string;
  publisher_tenant_id: string | null;
  publisher_name: string;
  source_slug: string;
  created_at: string;
  updated_at: string;
}

export interface PublishPayload {
  connection_slug: string;
  listing_slug?: string;
  description?: string;
  /** Publish into the Wise Exchange. Platform admins only. */
  official?: boolean;
}

/** Which slice of the exchange to browse. */
export type ExchangeScope = "all" | "official" | "community";

export function publishConnection(tenant: string, body: PublishPayload): Promise<ExchangeListing> {
  return request<ExchangeListing>(`/tenants/${tenant}/exchange/publish`, {
    method: "POST",
    body,
    auth: true,
  });
}

export function listExchange(scope: ExchangeScope = "all", q = ""): Promise<ExchangeListing[]> {
  const params = new URLSearchParams();
  if (scope !== "all") params.set("scope", scope);
  if (q.trim()) params.set("q", q.trim());
  const qs = params.toString();
  return request<ExchangeListing[]>(`/exchange${qs ? `?${qs}` : ""}`, { auth: true });
}

/** Catalog sections, in the order the Exchange shows them. */
export const CATEGORY_LABELS: Record<string, string> = {
  crm: "CRM & Sales",
  itsm: "IT Service Management",
  finance: "Finance & ERP",
  hr: "HR & Recruiting",
  productivity: "Productivity & Collaboration",
  devtools: "Developer Tools",
  observability: "Observability",
  data: "Data & Analytics",
  storage: "Storage",
  ecommerce: "E-commerce",
  marketing: "Marketing & Messaging",
  identity: "Identity & Signing",
  healthcare: "Healthcare",
  content: "Content & Social",
  generic: "Generic",
};

/** A catalog entry as shown on the public marketing site (no auth). */
export interface PublicListing {
  slug: string;
  display_name: string;
  description: string;
  system_type: string;
  icon_slug: string;
  brand_color: string;
  category: string;
  capability_count: number;
  param_count: number;
  has_code: boolean;
  install_count: number;
}

/** The Wise Exchange catalog, unauthenticated — used on the home page + /exchange. */
export function publicCatalog(limit = 24): Promise<PublicListing[]> {
  return request<PublicListing[]>(`/exchange/public?limit=${limit}`);
}

/** Promote a community listing into the Wise Exchange, or demote it. */
export function setListingOfficial(slug: string, official: boolean): Promise<ExchangeListing> {
  return request<ExchangeListing>(`/exchange/${slug}/official`, {
    method: "POST",
    body: { official },
    auth: true,
  });
}

export function getListing(slug: string): Promise<ExchangeListing> {
  return request<ExchangeListing>(`/exchange/${slug}`, { auth: true });
}

export function unpublishListing(tenant: string, listingSlug: string): Promise<void> {
  return request<void>(`/tenants/${tenant}/exchange/${listingSlug}`, {
    method: "DELETE",
    auth: true,
  });
}

export function installListing(
  tenant: string,
  listingSlug: string,
  body: { slug?: string; display_name?: string },
): Promise<Connection> {
  return request<Connection>(`/tenants/${tenant}/exchange/${listingSlug}/install`, {
    method: "POST",
    body,
    auth: true,
  });
}

// ---- workflows ----

export interface WorkflowStep {
  key: string;
  type: string;
  name: string;
  connection: string | null;
  capability: string | null;
  params: Record<string, unknown>;
}

export interface WorkflowTrigger {
  type: "manual" | "schedule" | "webhook";
  cron?: string;
  connection?: string;
  event?: string;
}

export interface Workflow {
  id: string;
  slug: string;
  name: string;
  description: string;
  trigger_type: string;
  trigger_summary: string;
  step_count: number;
  enabled: boolean;
  xml_version: number;
  last_run_at: string | null;
  last_run_status: string | null;
  created_at: string;
}

export interface WorkflowDetail extends Workflow {
  trigger: WorkflowTrigger;
  steps: WorkflowStep[];
}

export interface WorkflowSave {
  name: string;
  description?: string;
  trigger: WorkflowTrigger;
  steps: WorkflowStep[];
  enabled?: boolean;
}

/** A connection the builder can add as a step, with its operations. */
export interface BuilderConnection {
  slug: string;
  display_name: string;
  system_type: string;
  icon_hint: string;
  capabilities: string[];
  has_code: boolean;
}

export interface WorkflowRunStep {
  key: string;
  name: string;
  connection: string | null;
  capability: string | null;
  status: "ok" | "error" | "skipped" | "running";
  result?: unknown;
  error?: string;
}

export interface WorkflowRun {
  id: string;
  status: "running" | "succeeded" | "failed";
  trigger: string | null;
  steps: WorkflowRunStep[] | null;
  error: string | null;
  started_at: string;
  finished_at: string | null;
}

const wfBase = (tenant: string) => `/tenants/${tenant}/workflows`;

export function listWorkflows(tenant: string): Promise<Workflow[]> {
  return request<Workflow[]>(wfBase(tenant), { auth: true });
}

export function getWorkflow(tenant: string, slug: string): Promise<WorkflowDetail> {
  return request<WorkflowDetail>(`${wfBase(tenant)}/${slug}`, { auth: true });
}

export function builderConnections(tenant: string): Promise<BuilderConnection[]> {
  return request<BuilderConnection[]>(`${wfBase(tenant)}/builder/connections`, { auth: true });
}

export function createWorkflow(tenant: string, body: WorkflowSave): Promise<WorkflowDetail> {
  return request<WorkflowDetail>(wfBase(tenant), { method: "POST", body, auth: true });
}

export function updateWorkflow(
  tenant: string,
  slug: string,
  body: WorkflowSave,
): Promise<WorkflowDetail> {
  return request<WorkflowDetail>(`${wfBase(tenant)}/${slug}`, { method: "PUT", body, auth: true });
}

export function deleteWorkflow(tenant: string, slug: string): Promise<void> {
  return request<void>(`${wfBase(tenant)}/${slug}`, { method: "DELETE", auth: true });
}

export function runWorkflow(tenant: string, slug: string): Promise<WorkflowRun> {
  return request<WorkflowRun>(`${wfBase(tenant)}/${slug}/run`, { method: "POST", auth: true });
}

export function listRuns(tenant: string, slug: string): Promise<WorkflowRun[]> {
  return request<WorkflowRun[]>(`${wfBase(tenant)}/${slug}/runs`, { auth: true });
}

// ---- API designer / endpoints ----

export type AuthType = "none" | "basic" | "client_credentials" | "jwt";

export interface ApiEndpoint {
  id: string;
  slug: string;
  name: string;
  description: string;
  method: string;
  path: string;
  url: string;
  mode: "workflow" | "mock";
  workflow_slug: string | null;
  auth_type: AuthType;
  auth_config: Record<string, unknown>;
  request_schema: Record<string, unknown> | null;
  response_schema: Record<string, unknown> | null;
  mock_response: Record<string, unknown> | null;
  validate_request: boolean;
  enabled: boolean;
  call_count: number;
  last_called_at: string | null;
  created_at: string;
}

export interface ApiEndpointDetail extends ApiEndpoint {
  /** Plaintext credentials — present only right after create/update. */
  reveal: Record<string, string>;
  token_url: string | null;
}

export interface ApiEndpointSave {
  name: string;
  description?: string;
  method: string;
  path: string;
  mode?: "workflow" | "mock";
  workflow_slug?: string | null;
  auth_type: AuthType;
  auth?: Record<string, unknown>;
  request_schema?: Record<string, unknown> | null;
  response_schema?: Record<string, unknown> | null;
  mock_response?: Record<string, unknown> | null;
  validate_request?: boolean;
  enabled?: boolean;
}

/** A drafted endpoint from the AI designer. */
export interface DesignResult {
  name: string;
  method: string;
  path: string;
  description: string;
  request_schema: Record<string, unknown> | null;
  response_schema: Record<string, unknown> | null;
  mock_response: Record<string, unknown> | null;
}

const apiBase = (tenant: string) => `/tenants/${tenant}/apis`;

export function listEndpoints(tenant: string): Promise<ApiEndpoint[]> {
  return request<ApiEndpoint[]>(apiBase(tenant), { auth: true });
}

export function getEndpoint(tenant: string, slug: string): Promise<ApiEndpointDetail> {
  return request<ApiEndpointDetail>(`${apiBase(tenant)}/${slug}`, { auth: true });
}

export function createEndpoint(tenant: string, body: ApiEndpointSave): Promise<ApiEndpointDetail> {
  return request<ApiEndpointDetail>(apiBase(tenant), { method: "POST", body, auth: true });
}

export function updateEndpoint(
  tenant: string,
  slug: string,
  body: ApiEndpointSave,
): Promise<ApiEndpointDetail> {
  return request<ApiEndpointDetail>(`${apiBase(tenant)}/${slug}`, { method: "PUT", body, auth: true });
}

export function deleteEndpoint(tenant: string, slug: string): Promise<void> {
  return request<void>(`${apiBase(tenant)}/${slug}`, { method: "DELETE", auth: true });
}

/** Draft an endpoint from a natural-language prompt. */
export function designEndpoint(tenant: string, prompt: string): Promise<DesignResult> {
  return request<DesignResult>(`${apiBase(tenant)}/design`, {
    method: "POST",
    body: { prompt },
    auth: true,
  });
}

/** Preview what a mock endpoint would return. */
export function mockPreview(
  tenant: string,
  body: { response_schema?: unknown; mock_response?: unknown },
): Promise<{ status: number; body: unknown }> {
  return request(`${apiBase(tenant)}/mock-preview`, { method: "POST", body, auth: true });
}

export function openApiUrl(tenant: string): string {
  const base = (import.meta.env.VITE_API_BASE_URL ?? "/api").replace(/\/$/, "");
  return `${base}/gw/${tenant}/openapi.json`;
}
